import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import { Trash2 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Table, StatusBadge, SecondaryButton, SortableHeader, useTableSort } from "@/components/admin/AdminUI";
import { formatDate } from "@/lib/format";
import { ModalScroll } from "@/components/public/ModalScroll";
import { useState } from "react";

export default function AdminCrawlRuns() {
  const qc = useQueryClient();
  const { data: runs = [] } = useQuery({ queryKey: ["crawl-runs"], queryFn: async () => (await api.get("/admin/crawl-runs")).data });
  const [open, setOpen] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { sort, toggleSort, sortRows } = useTableSort();
  const sortedRuns = sortRows(runs, (r, key) => {
    if (key === "started_at") return r.started_at ? new Date(r.started_at).getTime() : null;
    if (key === "completed_at") return r.completed_at ? new Date(r.completed_at).getTime() : null;
    if (key === "total_products_found") return r.total_products_found || 0;
    if (key === "new_products_found") return r.new_products_found || 0;
    if (key === "duplicate_products_found") return r.duplicate_products_found || 0;
    return r[key] ?? null;
  });

  async function clearLogs() {
    const msg = `Delete all ${runs.length} crawl run log${runs.length === 1 ? "" : "s"}?\n\nThis only clears the log history. Discovered products (crawler results) and existing clothing items will NOT be touched.`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      const { data } = await api.delete("/admin/crawl-runs");
      toast.success(`Cleared ${data.deleted} log${data.deleted === 1 ? "" : "s"}.`);
      qc.invalidateQueries({ queryKey: ["crawl-runs"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear logs");
    } finally {
      setDeleting(false);
    }
  }

  async function markFailed(r) {
    if (!window.confirm(`Mark this ${r.status} run for ${r.brand_name} as failed?\n\nUse this when a run is stuck because the backend was restarted mid-crawl. Discovered products from earlier successful runs will NOT be touched.`)) return;
    try {
      await api.post(`/admin/crawl-runs/${r.id}/mark-failed`);
      toast.success("Marked as failed.");
      qc.invalidateQueries({ queryKey: ["crawl-runs"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark");
    }
  }

  return (
    <div>
      <PageHeader
        title="Crawl runs"
        subtitle={`${runs.length} runs`}
        actions={
          runs.length > 0 && (
            <SecondaryButton
              onClick={clearLogs}
              disabled={deleting}
              data-testid="clear-crawl-logs-button"
              className="text-clay hover:text-clay/80 border-clay/30"
            >
              <Trash2 className="w-4 h-4 inline mr-1" />
              {deleting ? "Clearing…" : "Clear logs"}
            </SecondaryButton>
          )
        }
      />
      <div className="bg-white border border-[#E9ECEF] rounded-lg overflow-hidden">
        <Table>
          <thead className="bg-[#F8F9FA] border-b border-[#E9ECEF] text-xs uppercase tracking-wider text-[#6C757D]">
            <tr>
              <SortableHeader label="Brand" sortKey="brand_name" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Started" sortKey="started_at" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Completed" sortKey="completed_at" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Found" sortKey="total_products_found" sort={sort} onSort={toggleSort} />
              <SortableHeader label="New" sortKey="new_products_found" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Duplicates" sortKey="duplicate_products_found" sort={sort} onSort={toggleSort} />
              <SortableHeader label="" />
            </tr>
          </thead>
          <tbody>
            {sortedRuns.map((r) => (
              <tr key={r.id} className="border-b border-[#E9ECEF]">
                <td className="px-3 py-3 font-medium">{r.brand_name}</td>
                <td className="px-3 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-3 py-3 text-xs">{formatDate(r.started_at)}</td>
                <td className="px-3 py-3 text-xs">{formatDate(r.completed_at)}</td>
                <td className="px-3 py-3 font-mono">{r.total_products_found || 0}</td>
                <td className="px-3 py-3 font-mono text-green-700">{r.new_products_found || 0}</td>
                <td className="px-3 py-3 font-mono">{r.duplicate_products_found || 0}</td>
                <td className="px-3 py-3 whitespace-nowrap">
                  <button onClick={() => setOpen(r)} className="underline text-sm" data-testid={`crawl-logs-${r.id}`}>Logs</button>
                  {(r.status === "running" || r.status === "queued") && (
                    <>
                      {" · "}
                      <button
                        onClick={() => markFailed(r)}
                        className="underline text-sm text-clay hover:text-clay/80"
                        data-testid={`crawl-mark-failed-${r.id}`}
                        title="Use this when a run is stuck because the backend restarted mid-crawl"
                      >
                        Mark failed
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {sortedRuns.length === 0 && <tr><td colSpan={8} className="px-3 py-12 text-center text-[#6C757D]">No crawl runs yet.</td></tr>}
          </tbody>
        </Table>
      </div>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white max-w-2xl w-full h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 pt-5 pb-3 border-b border-[#E9ECEF]">
              <h3 className="font-semibold">{open.brand_name} — log</h3>
            </div>
            <ModalScroll className="px-5 py-4">
              <pre className="text-xs whitespace-pre-wrap font-mono bg-[#F8F9FA] p-3">{(open.log || []).join("\n")}</pre>
              {open.error_message && <pre className="text-xs text-red-700 mt-2 whitespace-pre-wrap">{open.error_message}</pre>}
            </ModalScroll>
          </div>
        </div>
      )}
    </div>
  );
}
